/**
 * The menu bar.
 *
 * A small desktop-style composite menu: pointer input opens titles, while
 * roving title focus and standard arrow/Home/End/Escape keys keep the ARIA
 * menubar and menu roles honest without a framework or focus-trap library.
 *
 * Actions are looked up from a table by `data-action`, so adding an item is a
 * button in index.html plus one entry here.
 */
export function createMenuBar(actions) {
  const bar = document.getElementById('menubar');
  const menus = [...bar.querySelectorAll('.menu')];
  const titles = menus.map((menu) => menu.querySelector('.menu-title'));
  let currentTrigger = null;
  bar.setAttribute('role', 'menubar');

  function setOpen(menu, open) {
    menu.dataset.open = String(open);
    menu.querySelector('.menu-title')?.setAttribute('aria-expanded', String(open));
    menu.querySelector('.menu-items')?.setAttribute('aria-hidden', String(!open));
  }

  function setRoving(title) {
    for (const candidate of titles) candidate.tabIndex = candidate === title ? 0 : -1;
  }

  function itemsFor(menu) {
    return [...menu.querySelectorAll('.menu-item')].filter((item) => !item.disabled);
  }

  function closeAll({ restoreFocus = false, trigger = currentTrigger } = {}) {
    const focusWasInPopup = menus.some((menu) => menu.querySelector('.menu-items')?.contains(document.activeElement));
    for (const menu of menus) setOpen(menu, false);
    if ((restoreFocus || focusWasInPopup) && trigger) {
      setRoving(trigger);
      trigger.focus();
    }
    currentTrigger = null;
  }

  function openMenu(menu, focus = null) {
    const title = menu.querySelector('.menu-title');
    closeAll();
    currentTrigger = title;
    setRoving(title);
    setOpen(menu, true);
    const items = itemsFor(menu);
    if (focus === 'first') items[0]?.focus();
    if (focus === 'last') items.at(-1)?.focus();
  }

  function moveTitle(title, offset, preserveOpen = false) {
    const index = titles.indexOf(title);
    if (index < 0) return;
    const next = titles[(index + offset + titles.length) % titles.length];
    if (preserveOpen) openMenu(next.closest('.menu'));
    else setRoving(next);
    next.focus();
  }

  async function runAction(name, initiator = null) {
    const action = actions[name];
    if (!action) {
      console.warn(`[anvil] no handler for menu action '${name}'`);
      return;
    }

    try {
      await action(initiator);
    } catch (err) {
      console.error(`[anvil] menu action '${name}' failed`, err);
      alert(err?.message ?? String(err));
    }
  }

  for (const menu of menus) {
    const title = menu.querySelector('.menu-title');
    const popup = menu.querySelector('.menu-items');
    const popupId = `${menu.dataset.menu}-menu`;
    popup.id = popupId;
    popup.setAttribute('role', 'menu');
    popup.setAttribute('aria-hidden', 'true');
    title.setAttribute('role', 'menuitem');
    title.setAttribute('aria-haspopup', 'menu');
    title.setAttribute('aria-controls', popupId);
    title.setAttribute('aria-expanded', 'false');
    title.tabIndex = title === titles[0] ? 0 : -1;
    for (const separator of popup.querySelectorAll('.menu-separator')) separator.setAttribute('role', 'separator');
    for (const item of popup.querySelectorAll('.menu-item')) {
      item.setAttribute('role', 'menuitem');
      item.setAttribute('aria-disabled', String(item.disabled));
      item.tabIndex = -1;
    }
    title.addEventListener('click', (event) => {
      event.stopPropagation();
      const wasOpen = menu.dataset.open === 'true';
      if (wasOpen) closeAll({ restoreFocus: true, trigger: title });
      else openMenu(menu);
    });

    // Once one menu is open, sliding across the bar should follow — the way
    // every desktop menu bar behaves.
    title.addEventListener('mouseenter', () => {
      if (menus.some((other) => other.dataset.open === 'true')) {
        const movePopupFocus = Boolean(document.activeElement?.closest?.('.menu-items'));
        openMenu(menu, movePopupFocus ? 'first' : null);
      }
    });
  }

  bar.addEventListener('click', async (event) => {
    const item = event.target.closest('.menu-item');
    if (!item || item.disabled) return;
    const trigger = item.closest('.menu')?.querySelector('.menu-title') ?? currentTrigger;
    closeAll();

    await runAction(item.dataset.action, item);
    if (trigger) {
      setRoving(trigger);
      trigger.focus();
    }
  });

  document.addEventListener('click', closeAll);
  document.addEventListener('keydown', (event) => {
    const title = event.target.closest?.('.menu-title');
    const item = event.target.closest?.('.menu-item');
    const openMenuElement = menus.find((menu) => menu.dataset.open === 'true');

    if (title && title.closest('#menubar') === bar) {
      const menu = title.closest('.menu');
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        moveTitle(title, event.key === 'ArrowRight' ? 1 : -1, Boolean(openMenuElement));
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const next = event.key === 'Home' ? titles[0] : titles.at(-1);
        if (openMenuElement) openMenu(next.closest('.menu'));
        else setRoving(next);
        next.focus();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openMenu(menu, event.key === 'ArrowDown' ? 'first' : 'last');
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (menu.dataset.open === 'true') closeAll({ restoreFocus: true, trigger: title });
        else openMenu(menu, 'first');
        return;
      }
    }

    if (item && item.closest('#menubar') === bar) {
      const menu = item.closest('.menu');
      const items = itemsFor(menu);
      const index = items.indexOf(item);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const offset = event.key === 'ArrowDown' ? 1 : -1;
        items[(index + offset + items.length) % items.length]?.focus();
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        (event.key === 'Home' ? items[0] : items.at(-1))?.focus();
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const menuIndex = menus.indexOf(menu);
        const next = menus[(menuIndex + (event.key === 'ArrowRight' ? 1 : -1) + menus.length) % menus.length];
        openMenu(next, 'first');
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        item.click();
        return;
      }
    }

    if (event.key === 'Escape' && openMenuElement) {
      event.preventDefault();
      closeAll({ restoreFocus: true });
    } else if (event.key === 'Tab' && openMenuElement) {
      closeAll({ restoreFocus: true });
    }
  });

  return { closeAll };
}

/** Prompt a download of some text as a file. */
export function downloadText(filename, text, type = 'application/json') {
  downloadBlob(filename, new Blob([text], { type }));
}

/** Prompt a download of binary content without involving the bridge server. */
export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Revoking synchronously can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Convert a base64 command result to a Blob and prompt its download. */
export function downloadBase64(filename, base64, type = 'application/octet-stream') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
  downloadBlob(filename, new Blob([bytes], { type }));
}

/** Ask for a file through the hidden input and resolve with its text. */
export function pickTextFile() {
  const input = document.getElementById('scene-file');
  return new Promise((resolve) => {
    const onChange = () => {
      const file = input.files?.[0];
      input.removeEventListener('change', onChange);
      // Reset so picking the same file twice still fires a change event.
      input.value = '';
      if (!file) {
        resolve(null);
        return;
      }
      file.text().then(resolve);
    };
    input.addEventListener('change', onChange);
    input.click();
  });
}
